import Dropzone from "vendor/dropzone";
import _ from "underscore";

import View from "backdraft-app/src/view";

import Ajax from "shared/ajax";
import Application from "shared/application";

import previewTemplate from "./dropzone/preview.jst";

export default class DropzoneView extends View {
  get body() { return '<div class="dropzone" data-mount="dropzone"></div>'; }

  initialize(options) {
    super.initialize(options);

    this._validateOptions(options);
    this._setPropertiesFromOptions(options);
    this.options = options;
  }

  render() {
    super.render();

    this.$el.html(this.options.body || this.body);
    this._setupDropzone();
    return this;
  }

  fileAdded() {
    if (this.dropzone) {
      return (this.dropzone.files.length > 0);
    }
  }

  submitFile() {
    this.dropzone.processQueue();
  }

  removeFiles() {
    this.dropzone.removeAllFiles();
  }

  _validateOptions(options) {
    if (!options.uploadDestinationUrl) {
      throw new Error("uploadDestinationUrl is required");
    }

    const dropzoneModes = ["spreadsheet", "excel", "image"];
    if (!_.contains(dropzoneModes, options.mode)) {
      throw new Error(`mode should be set to one of these: ${dropzoneModes.join(", ")}`);
    }
  }

  _setPropertiesFromOptions(options) {
    this.mode = options.mode;
    this.uploadDestinationUrl = options.uploadDestinationUrl;
    this.paramName = options.paramName;
  }

  _setupDropzone() {
    const dropzoneView = this;
    const dropzoneOptions = {
      url: dropzoneView.uploadDestinationUrl,
      uploadMultiple: false,
      addRemoveLinks: true,
      clickable: true,
      maxFiles: 1,
      autoProcessQueue: false,
      autoQueue: _.isUndefined(this.options.autoQueue) ? true : this.options.autoQueue,
      headers: { 'X-CSRF-Token': Application.getCSRFToken() },
      paramName: this.paramName || "upload_file",
      dictDefaultMessage: this._computeDictDefaultMessage(),
      previewTemplate: previewTemplate(),
      acceptedFiles: this._computeAcceptedFiles(),
      dictInvalidFileType: this._computeDictInvalidFileType(),
      init() {
        // This ensures only one file will be uploaded if several files are dropped into the DropZone.
        dropzoneView.trigger('init', this);
        this.on("addedfile", function(file) {
          if (typeof this.files[1] !== "undefined") {
            this.removeFile(this.files[0]);
          }
          dropzoneView.trigger("added_file", file);
        });

        this.on("removedfile", file => {
          dropzoneView.trigger("removed_file", file);
        });

        this.on("thumbnail", (file, image) => {
          dropzoneView.trigger("thumbnail", file, image);
        });

        this.on("sending", (file, xhr, formData) => {
          dropzoneView.trigger("sending_file", file, xhr, formData);
        });

        this.on("complete", file => {
          dropzoneView.trigger("complete", file);
        });

        this.on("success", function(file, response) {
          this.removeAllFiles();
          dropzoneView.trigger("success", file, response);
        });
      },

      // Dropzone attaches client-side and server-side errors to the text of the element with the 'data-dz-errormessage'
      // attribute. However, Dropzone is not able to properly parse server-side errors. As a result, we have to manually
      // parse server-side errors and attach them to the text of the element with the 'data-dz-errormessage' attribute
      error(file, message, xhr) {
        let errorMessage = "";
        if (xhr) {
          errorMessage = Ajax.errorMessages(xhr).join(', ');
        } else {
          errorMessage = message;
        }

        // update the previewElement - this is a copy of dropzone's default error handler
        if (file.previewElement) {
          file.previewElement.classList.add("dz-error");

          dropzoneView.$("[data-dz-errormessage]").text(errorMessage);
        }

        dropzoneView.trigger("error", file, message, xhr);
      }
    };
    this.dropzone = new Dropzone(this.$("[data-mount='dropzone']")[0], dropzoneOptions);
  }

  _computeDictDefaultMessage() {
    switch (this.mode) {
    case "spreadsheet":
      return "Click to browse, or drag an .xlsx, .xls or .csv file into this box.";
    case "excel":
      return "Click to browse, or drag an .xlsx or .xls file into this box.";
    case "image":
      return "Click to browse, or drag a .jpg, .jpeg, .gif, or .png file into this box.";
    default:
      throw new Error(`could not compute dictDefaultMessage for '${this.mode}' mode`);
    }
  }

  _computeDictInvalidFileType() {
    switch (this.mode) {
    case "spreadsheet":
      return "Only .xlsx, .xls and .csv file formats are supported.";
    case "excel":
      return "Only .xlsx and .xls file formats are supported.";
    case "image":
      return "Only .jpg, .jpeg, .gif, and .png file formats are supported.";
    default:
      throw new Error(`could not compute dictInvalidFileType for '${this.mode}' mode`);
    }
  }

  _computeAcceptedFiles() {
    const missingContentType = ''; // Windows 10 does not supply a file content type for excel nor csv, so we will not enforce client side validations.
    const acceptedExcel = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', missingContentType];
    const acceptedCsv = ['text/csv', 'text/comma-separated-values', missingContentType];
    const acceptedSpreadsheet = acceptedExcel.concat(acceptedCsv);
    const acceptedImage = ['image/jpg', 'image/jpeg', 'image/png', 'image/gif'];

    const separator = ', ';

    switch (this.mode) {
    case "spreadsheet":
      return acceptedSpreadsheet.concat(acceptedCsv).join(separator);
    case "excel":
      return acceptedExcel.join(separator);
    case "image":
      return acceptedImage.join(separator);
    default:
      throw new Error(`could not compute acceptedFiles for '${this.mode}' mode`);
    }
  }
}
